// ensureAllowance.js
import { erc20Abi } from "viem";

/**
 * Ensures ERC-20 allowance >= amount.
 * Uses the safe pattern: approve(0) -> approve(amount), with simulation first.
 * @returns {Promise<boolean>} true if allowance is sufficient / set successfully
 */
export default async function ensureAllowance({
  account, // "0xabc..."
  publicClient, // from wagmi's usePublicClient()
  walletClient, // from wagmi's useWalletClient()
  token, // token address
  spender, // contract that will spend the tokens
  amount, // bigint (e.g., parseUnits("100", 18))
}) {
  const current = await publicClient.readContract({
    abi: erc20Abi,
    address: token,
    functionName: "allowance",
    args: [account, spender],
  });

  if (current >= amount) return true;

  // Some tokens require approve(0) before a new non-zero approval
  if (current > 0n) {
    const { request } = await publicClient.simulateContract({
      abi: erc20Abi,
      address: token,
      functionName: "approve",
      args: [spender, 0n],
      account,
      value: 0n,
    });
    const hash0 = await walletClient.writeContract(request);
    await publicClient.waitForTransactionReceipt({ hash: hash0 });
  }

  const { request } = await publicClient.simulateContract({
    abi: erc20Abi,
    address: token,
    functionName: "approve",
    args: [spender, amount],
    account,
    value: 0n,
  });
  const hash1 = await walletClient.writeContract(request);
  const receipt = await publicClient.waitForTransactionReceipt({ hash: hash1 });

  return receipt.status === "success";
}
